import type {
  Alert,
  ApiToken,
  ApiTokenCreated,
  Asset,
  AuditEntry,
  BrandConfig,
  CloneAction,
  CloneCreate,
  CloneDetail,
  ClonePatch,
  CloneSummary,
  EnvField,
  HealthSnapshot,
  Job,
  JobLogLine,
  LoginAnswer,
  LogLine,
  MeAnswer,
  Overview,
  Release,
  Settings,
  StatsSnapshot,
  TenantAnswers,
  User,
  UserCreated,
} from '@api/api';
import { api, qs } from './client';
import type { Range } from './types';

export interface LogQuery {
  service?: string;
  since?: string;
  level?: string;
  q?: string;
  tail?: number;
}

export const endpoints = {
  // auth
  me: () => api.get<MeAnswer>('/auth/me', { noAuthRedirect: true }),
  login: (email: string, password: string) => api.post<LoginAnswer>('/auth/login', { email, password }, { noAuthRedirect: true }),
  totpEnroll: () => api.post<{ otpauthUrl: string; qrSvg: string }>('/auth/totp/enroll', {}, { noAuthRedirect: true }),
  totpVerify: (code: string) => api.post<{ user: User }>('/auth/totp/verify', { code }, { noAuthRedirect: true }),
  changePassword: (currentPassword: string, newPassword: string) => api.post<{ ok: true }>('/auth/password', { currentPassword, newPassword }, { noAuthRedirect: true }),
  logout: () => api.post<{ ok: true }>('/auth/logout', {}, { noAuthRedirect: true }),

  overview: () => api.get<Overview>('/overview'),

  // clones
  clones: () => api.get<CloneSummary[]>('/clones'),
  clone: (id: number) => api.get<CloneDetail>(`/clones/${id}`),
  createClone: (body: CloneCreate) => api.post<CloneDetail>('/clones', body),
  patchClone: (id: number, body: ClonePatch) => api.patch<CloneDetail>(`/clones/${id}`, body),
  env: (id: number) => api.get<EnvField[]>(`/clones/${id}/env`),
  putEnv: (id: number, values: Record<string, string | null>) => api.put<EnvField[]>(`/clones/${id}/env`, { values }),
  revealEnv: (id: number, key: string) => api.post<{ value: string }>(`/clones/${id}/env/${encodeURIComponent(key)}/reveal`, {}),
  tenant: (id: number) => api.get<TenantAnswers | null>(`/clones/${id}/tenant`),
  putTenant: (id: number, body: TenantAnswers) => api.put<TenantAnswers>(`/clones/${id}/tenant`, body),
  brand: (id: number) => api.get<BrandConfig | null>(`/clones/${id}/brand`),
  putBrand: (id: number, body: BrandConfig) => api.put<BrandConfig>(`/clones/${id}/brand`, body),
  assets: (id: number) => api.get<Asset[]>(`/clones/${id}/assets`),
  uploadAsset: (id: number, kind: string, file: File) => {
    const fd = new FormData();
    fd.append('kind', kind);
    fd.append('file', file);
    return api.post<Asset>(`/clones/${id}/assets`, fd);
  },
  deleteAsset: (id: number, assetId: number) => api.del<void>(`/clones/${id}/assets/${assetId}`),
  brandPackageUrl: (id: number) => `/api/clones/${id}/brand-package`,
  action: (id: number, action: CloneAction, body: Record<string, unknown> = {}) => api.post<Job>(`/clones/${id}/actions/${action}`, body),
  health: (id: number, range: Range) => api.get<{ snapshots: HealthSnapshot[] }>(`/clones/${id}/health${qs({ range })}`),
  stats: (id: number, range: Range) => api.get<{ snapshots: StatsSnapshot[] }>(`/clones/${id}/stats${qs({ range })}`),
  logs: (id: number, q: LogQuery) => api.get<{ lines: LogLine[] }>(`/clones/${id}/logs${qs({ ...q })}`),
  logStreamPath: (id: number, q: LogQuery) => `/clones/${id}/logs/stream${qs({ ...q })}`,
  cloneJobs: (id: number) => api.get<Job[]>(`/clones/${id}/jobs`),

  // jobs
  jobs: (params: { status?: string; cloneId?: number } = {}) => api.get<Job[]>(`/jobs${qs(params)}`),
  job: (id: number) => api.get<Job>(`/jobs/${id}`),
  jobLog: (id: number) => api.get<{ lines: JobLogLine[] }>(`/jobs/${id}/log`),
  jobStreamPath: (id: number) => `/jobs/${id}/stream`,
  retryJob: (id: number) => api.post<Job>(`/jobs/${id}/retry`, {}),

  // releases
  releases: () => api.get<Release[]>('/releases'),
  /** Creates the release and enqueues its release-fetch job (Release.fetchJobId). */
  createRelease: (body: { name: string; backendRef: string; crmRef: string; notes?: string }) => api.post<Release>('/releases', body),
  deleteRelease: (id: number) => api.del<void>(`/releases/${id}`),

  // alerts
  alerts: (params: { open?: boolean; cloneId?: number } = {}) =>
    api.get<Alert[]>(`/alerts${qs({ open: params.open === undefined ? undefined : params.open ? 1 : 0, cloneId: params.cloneId })}`),
  ackAlert: (id: number) => api.post<Alert>(`/alerts/${id}/ack`, {}),

  // settings
  settings: () => api.get<Settings>('/settings'),
  putSettings: (body: Partial<Settings>) => api.put<Settings>('/settings', body),
  testTelegram: () => api.post<{ ok: boolean }>('/settings/telegram/test', {}),
  users: () => api.get<User[]>('/users'),
  createUser: (body: { email: string; role: User['role'] }) => api.post<UserCreated>('/users', body),
  patchUser: (id: number, body: { role?: User['role']; disabled?: boolean; resetTotp?: boolean; resetPassword?: boolean }) => api.patch<{ user: User; password?: string }>(`/users/${id}`, body),
  deleteUser: (id: number) => api.del<void>(`/users/${id}`),
  tokens: () => api.get<ApiToken[]>('/tokens'),
  createToken: (name: string) => api.post<ApiTokenCreated>('/tokens', { name }),
  deleteToken: (id: number) => api.del<void>(`/tokens/${id}`),
  audit: (limit = 200) => api.get<AuditEntry[]>(`/audit${qs({ limit })}`),
};

export const qk = {
  me: ['me'] as const,
  overview: ['overview'] as const,
  clones: ['clones'] as const,
  clone: (id: number) => ['clone', id] as const,
  env: (id: number) => ['clone', id, 'env'] as const,
  tenant: (id: number) => ['clone', id, 'tenant'] as const,
  brand: (id: number) => ['clone', id, 'brand'] as const,
  assets: (id: number) => ['clone', id, 'assets'] as const,
  health: (id: number, r: Range) => ['clone', id, 'health', r] as const,
  stats: (id: number, r: Range) => ['clone', id, 'stats', r] as const,
  cloneJobs: (id: number) => ['clone', id, 'jobs'] as const,
  jobs: ['jobs'] as const,
  job: (id: number) => ['job', id] as const,
  jobLog: (id: number) => ['job', id, 'log'] as const,
  releases: ['releases'] as const,
  alerts: (open: boolean) => ['alerts', open] as const,
  settings: ['settings'] as const,
  users: ['users'] as const,
  tokens: ['tokens'] as const,
  audit: ['audit'] as const,
};
